const STORAGE_KEY = 'campusPaymentCharges';
const SESSIONS_KEY = 'campusSessions';
const RESUME_KEY = 'paymentResumeState';
const OWNER_KEY = 'campusPaymentOwner';

const CAMPUS_KEYS = [STORAGE_KEY, SESSIONS_KEY, RESUME_KEY];

/**
 * Campus payment progress is kept in localStorage rather than sessionStorage so
 * a family that pays one campus and closes the browser can still be routed back
 * to the payment page for the remaining campuses on their next login.
 * sessionStorage is still read as a fallback so payments already in flight when
 * this changed are not stranded.
 */
const parse = (raw, fallback) => {
    try {
        const parsed = JSON.parse(raw);
        return parsed == null ? fallback : parsed;
    } catch (e) {
        return fallback;
    }
};

const readJson = (key, fallback) => {
    try {
        const durable = localStorage.getItem(key);
        if (durable) return parse(durable, fallback);
    } catch (e) {
        // ignore storage failures
    }

    try {
        const legacy = sessionStorage.getItem(key);
        if (legacy) return parse(legacy, fallback);
    } catch (e) {
        // ignore storage failures
    }

    return fallback;
};

const writeJson = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // ignore storage failures
    }

    try {
        sessionStorage.removeItem(key);
    } catch (e) {
        // ignore storage failures
    }
};

const removeKey = (key) => {
    try {
        localStorage.removeItem(key);
    } catch (e) {
        // ignore
    }

    try {
        sessionStorage.removeItem(key);
    } catch (e) {
        // ignore
    }
};

export const storeCampusPaymentCharges = (charges) => {
    writeJson(STORAGE_KEY, Array.isArray(charges) ? charges : []);
};

export const getCampusPaymentCharges = () => {
    const list = readJson(STORAGE_KEY, []);
    return Array.isArray(list) ? list : [];
};

export const clearCampusPaymentCharges = () => {
    removeKey(STORAGE_KEY);
};

export const storeCampusSessions = (sessions) => {
    writeJson(SESSIONS_KEY, Array.isArray(sessions) ? sessions : []);
};

export const getCampusSessions = () => {
    const list = readJson(SESSIONS_KEY, []);
    return Array.isArray(list) ? list : [];
};

export const storePaymentResumeState = (state) => {
    writeJson(RESUME_KEY, state && typeof state === 'object' ? state : {});
};

export const getPaymentResumeState = () => {
    const state = readJson(RESUME_KEY, {});
    return state && typeof state === 'object' ? state : {};
};

export const clearPaymentResumeState = () => {
    removeKey(RESUME_KEY);
};

/** Drops every campus payment trace — used on logout and on parent switch. */
export const clearCampusPaymentState = () => {
    CAMPUS_KEYS.forEach(removeKey);
    removeKey(OWNER_KEY);
};

/**
 * Ties stored campus payment progress to one parent code, so a second family
 * logging in on a shared device never inherits the first family's charges.
 */
export const setCampusPaymentOwner = (parentId) => {
    const owner = parentId == null ? '' : String(parentId);
    if (!owner) return;

    let previous = null;
    try {
        previous = localStorage.getItem(OWNER_KEY);
    } catch (e) {
        previous = null;
    }

    if (previous && previous !== owner) {
        clearCampusPaymentState();
    }

    try {
        localStorage.setItem(OWNER_KEY, owner);
    } catch (e) {
        // ignore storage failures
    }
};

export const markCampusChargePaid = (sessionId) => {
    const charges = getCampusPaymentCharges().map((row) => {
        if (String(row.SessionID) === String(sessionId)) {
            return { ...row, paymentStatus: 'paid' };
        }
        return row;
    });
    storeCampusPaymentCharges(charges);
    return charges;
};

export const markCampusChargeFailed = (sessionId) => {
    const charges = getCampusPaymentCharges().map((row) => {
        if (String(row.SessionID) === String(sessionId)) {
            return { ...row, paymentStatus: 'failed' };
        }
        return row;
    });
    storeCampusPaymentCharges(charges);
    return charges;
};
